import { useCallback, useMemo, useState } from 'react'

import { Group } from '../types'
import { sortObjectsAlphabetically } from '../lib/sort'
import { useCurrentUser } from './useCurrentUser'
import { useStateCache } from '../lib/localstorage'
import useApi from './useApi'

export default function useGroups() {
  const { currentUser } = useCurrentUser()
  const { getCurrentUserGroups } = useApi()

  const [groupsState, setGroups] = useStateCache('groups', [])
  const groups = Array.isArray(groupsState) ? groupsState : []
  const [initialLoad, setInitialLoad] = useState(groups.length === 0)
  const [isLoading, setIsLoading] = useState(false)
  const [waitlistState, setWaitlistGroups] = useStateCache('waitlistGroups', [])
  const waitlistGroups = Array.isArray(waitlistState) ? waitlistState : []

  const fetchGroups = useCallback(async () => {
    const { data } = await getCurrentUserGroups()
    setGroups(Array.isArray(data) ? data : [])
  }, [getCurrentUserGroups, setGroups])

  const fetchWaitlistGroups = useCallback(async () => {
    const w = currentUser?.waitlist
    setWaitlistGroups(Array.isArray(w) ? w : [])
  }, [currentUser, setWaitlistGroups])

  const fetchAllGroups = useCallback(async () => {
    setIsLoading(true)
    await Promise.all([fetchGroups(), fetchWaitlistGroups()])
    setInitialLoad(false)
    setIsLoading(false)
  }, [fetchGroups, fetchWaitlistGroups])

  /**
   * An array of all groups the user is a member of, including waitlist groups.
   * Groups are sorted alphabetically by name.
   */
  const allGroups = useMemo(() => {
    return sortObjectsAlphabetically(
      [...groups, ...waitlistGroups],
      'name',
    ) as Group[]
  }, [groups, waitlistGroups])

  return {
    allGroups,
    fetchAllGroups,
    fetchGroups,
    fetchWaitlistGroups,
    groups,
    initialLoad,
    isLoading,
    waitlistGroups,
  }
}
